import React from "react"
import PropTypes from "prop-types"
import { Button, Chip, Rating } from "@mui/material"
import {
    PlayCircleOutline,
    CheckCircle,
    PersonOutline
} from "@mui/icons-material"
import { formatPrice } from "./price"
import { formatAccessUntil } from "./access"

/**
 * One course in the catalogue or in My Courses.
 *
 * The artwork is generated from the course's `accent` colour rather than an
 * uploaded image, so a newly created course looks finished straight away.
 */
export default function CourseCard({ course, onOpen, onBuy }) {
    const owned = Boolean(course.is_enrolled)
    const accessUntil = formatAccessUntil(course.expires_at)
    const accent = course.accent || "#4f46e5"

    return (
        <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">

            {/* Generated cover */}
            <button
                type="button"
                onClick={() => onOpen(course)}
                className="relative flex h-28 w-full items-center justify-center sm:h-32"
                style={{
                    background: `linear-gradient(135deg, ${accent} 0%, ${accent}bb 55%, #0f172a 100%)`
                }}
            >
                <span className="px-4 text-center text-base font-bold text-white/95 sm:text-lg">
                    {course.subject_name}
                </span>
                {owned && (
                    <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                        <CheckCircle sx={{ fontSize: 13 }} />
                        Enrolled
                    </span>
                )}
            </button>

            <div className="flex flex-1 flex-col p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-2">
                    {course.class_name && (
                        <Chip size="small" label={course.class_name} />
                    )}
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                        <PlayCircleOutline sx={{ fontSize: 14 }} />
                        {course.lecture_count || 0}{" "}
                        {course.lecture_count === 1 ? "lecture" : "lectures"}
                    </span>
                </div>

                <button
                    type="button"
                    onClick={() => onOpen(course)}
                    className="mt-2 text-left"
                >
                    <h3 className="line-clamp-2 text-sm font-semibold text-slate-800 sm:text-base">
                        {course.title}
                    </h3>
                </button>

                {course.description && (
                    <p className="mt-1.5 line-clamp-2 text-xs text-slate-500 sm:text-sm">
                        {course.description}
                    </p>
                )}

                {course.instructor && (
                    <p className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                        <PersonOutline sx={{ fontSize: 14 }} />
                        {course.instructor}
                    </p>
                )}

                {/* Rating, Udemy style: "4.6 ★★★★★ (128)" */}
                {course.rating_count > 0 && (
                    <p className="mt-1.5 flex items-center gap-1 text-xs">
                        <span className="font-bold text-amber-700">
                            {Number(course.rating_average || 0).toFixed(1)}
                        </span>
                        <Rating
                            value={Number(course.rating_average) || 0}
                            precision={0.1}
                            size="small"
                            readOnly
                            sx={{ fontSize: 14 }}
                        />
                        <span className="text-slate-400">
                            ({course.rating_count})
                        </span>
                    </p>
                )}

                {/* Price + action pinned to the bottom so cards line up */}
                <div className="mt-auto flex items-center justify-between gap-3 pt-4">
                    {owned && accessUntil ? (
                        <span className="text-xs text-slate-500">
                            Access until{" "}
                            <span className="font-semibold text-slate-700">
                                {accessUntil}
                            </span>
                        </span>
                    ) : (
                        <span
                            className={`text-base font-bold sm:text-lg ${
                                Number(course.price) > 0
                                    ? "text-slate-900"
                                    : "text-emerald-600"
                            }`}
                        >
                            {formatPrice(course.price, course.currency)}
                        </span>
                    )}

                    {owned ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onOpen(course)}
                        >
                            Continue
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => onBuy(course)}
                        >
                            {Number(course.price) > 0 ? "Buy now" : "Enrol free"}
                        </Button>
                    )}
                </div>
            </div>
        </article>
    )
}

CourseCard.propTypes = {
    course: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
    onBuy: PropTypes.func.isRequired
}
